import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import {
  digObject, matchFilterArrayIncludes, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'

// @ts-expect-error TS2724
import type { ServiceProductModel, ServiceProductRequestOptions } from '@models/types'

const watchEntityKeys = ['serviceProducts']

type ServiceProductFilters = {
  assigned_provider_id?: number,
  locked_status?: boolean,
  service_id?: string,
  service_product_ids?: number[],
  string?: string,
}

type UseServiceProductsOptions = {
  filters?: ServiceProductFilters,
  performHttpRequests?: boolean,
  requestOptions?: ServiceProductRequestOptions,
}

function useServiceProducts(options: UseServiceProductsOptions) {
  const { filters, requestOptions } = options
  const {
    assigned_provider_id: filterAssignedProviderId,
    locked_status: filterLockedStatus,
    service_id: filterServiceId,
    service_product_ids: filterServiceProductIds,
    string,
  } = filters || {}

  const {
    updatedEntities: { serviceProducts: serviceProductsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  // @ts-expect-error TS18046
  const serviceProducts: ServiceProductModel[] = useSelector(reduxState => reduxState.entities.serviceProducts)

  const filteredServiceProducts = useMemo(() => {
    const filtered = Object.values(serviceProducts).filter((serviceProduct) => {
      const {
        id,
        provider_ids = [],
        service_id,
        title,
      } = serviceProduct

      const locked_status = digObject(serviceProduct, 'options.locked', false)
      const matchId = id.toString() === string
      const matchLockedStatus = filterLockedStatus ? locked_status.toString() === filterLockedStatus : true
      // @ts-expect-error TS2345
      const matchServiceId = matchFilterNumber(service_id, filterServiceId)

      const matchAssignedProvider = matchFilterArrayIncludes(provider_ids, filterAssignedProviderId)
      const matchTitle = matchFilterString(title, string)
      const matchString = matchId || matchTitle
      const matchServiceProductIds = filterServiceProductIds
        ? matchFilterArrayIncludes(filterServiceProductIds, id)
        : true

      return matchServiceId && matchString && matchLockedStatus && matchAssignedProvider && matchServiceProductIds
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [serviceProductsUpdatedAt, JSON.stringify(filters)])

  const filteredServiceProductsCount = filteredServiceProducts.length
  const hasFilteredServiceProducts = !!filteredServiceProducts.length

  const loadMorePayload = useLoadMore({
    loadedCount: filteredServiceProductsCount,
    filters,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingProducts } = useReduxAction(
    'serviceProducts',
    'loadServiceProducts',
    {
      ...filtersWithOffset,
      limit,
      ...requestOptions,
    },
    [performHttpRequests, filtersWithOffset],
    // @ts-expect-error TS2339
    { shouldPerformFn: ({ loading }) => performHttpRequests && !loading },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredServiceProducts,
    filteredServiceProductsCount,
    hasFilteredServiceProducts,
    loading: loadingProducts,
  }
}

export default useServiceProducts
